import { useState, useMemo } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import PipelineColumn from './PipelineColumn';
import { Lead, LeadCardPreview } from './LeadCard';
import { validateStageTransition, ValidationResult } from './stageValidation';
import type { LeadStatus } from './stageValidation';

interface LeadActivityCounts {
  [leadId: string]: {
    total: number;
    hasMeeting: boolean;
  };
}

interface LeadsPipelineProps {
  leads: Lead[];
  activityCounts?: LeadActivityCounts;
  onStatusChange: (lead: Lead, newStatus: LeadStatus) => void;
  onValidationFailed: (lead: Lead, targetStatus: LeadStatus, validation: ValidationResult) => void;
  onEditLead: (lead: Lead) => void;
  onViewLead: (lead: Lead) => void;
  onViewActivity: (lead: Lead) => void;
  getAssigneeName?: (userId: string | null) => string | null;
}

const STATUSES: LeadStatus[] = ['new', 'contacted', 'demo_scheduled', 'demo_done', 'converted', 'lost'];

export default function LeadsPipeline({
  leads,
  activityCounts = {},
  onStatusChange,
  onValidationFailed,
  onEditLead,
  onViewLead,
  onViewActivity,
  getAssigneeName,
}: LeadsPipelineProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Group leads by status
  const leadsByStatus = useMemo(() => {
    const grouped: Record<LeadStatus, Lead[]> = {
      new: [],
      contacted: [],
      demo_scheduled: [],
      demo_done: [],
      converted: [],
      lost: [],
    };

    leads.forEach((lead) => {
      if (grouped[lead.status]) {
        grouped[lead.status].push(lead);
      }
    });

    return grouped;
  }, [leads]);

  // Find the active lead for drag overlay
  const activeLead = useMemo(() => {
    if (!activeId) return null;
    return leads.find((lead) => lead.id === activeId) || null;
  }, [activeId, leads]);

  // Configure sensors for mouse and touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const leadId = active.id as string;
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) return;

    // Determine target status
    let targetStatus: LeadStatus;

    if (STATUSES.includes(over.id as LeadStatus)) {
      targetStatus = over.id as LeadStatus;
    } else {
      const targetLead = leads.find((l) => l.id === over.id);
      if (!targetLead) return;
      targetStatus = targetLead.status;
    }

    // Don't do anything if status hasn't changed
    if (lead.status === targetStatus) return;

    // Validate the transition
    const counts = activityCounts[lead.id] || { total: 0, hasMeeting: false };
    const validation = validateStageTransition(
      lead,
      targetStatus,
      counts.total,
      counts.hasMeeting
    );

    if (validation.canMove) {
      onStatusChange(lead, targetStatus);
    } else {
      onValidationFailed(lead, targetStatus, validation);
    }
  };

  const handleDragOver = (_event: DragOverEvent) => {
    // Could be used for visual feedback during drag
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <div className="leads-pipeline-content">
        {STATUSES.map((status) => (
          <PipelineColumn
            key={status}
            status={status}
            leads={leadsByStatus[status]}
            onEditLead={onEditLead}
            onViewLead={onViewLead}
            onViewActivity={onViewActivity}
            getAssigneeName={getAssigneeName}
          />
        ))}
      </div>

      <DragOverlay>
        {activeLead ? <LeadCardPreview lead={activeLead} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
